import Phaser from 'phaser';
import { Soldier } from '../entities/Soldier';

const TILE_SIZE = 32;

// Formation offsets for up to 4 soldiers (spread 32px apart)
const FORMATION_OFFSETS: { x: number; y: number }[] = [
  { x: 0, y: 0 },
  { x: TILE_SIZE, y: 0 },
  { x: 0, y: TILE_SIZE },
  { x: TILE_SIZE, y: TILE_SIZE },
];

export class SquadManager {
  private soldiers: Soldier[] = [];
  private selected: Set<Soldier> = new Set();
  // Drag-select state
  private dragStart: Phaser.Math.Vector2 | null = null;
  private dragRect!: Phaser.GameObjects.Graphics;
  private isDragging: boolean = false;
  private readonly DRAG_THRESHOLD = 8; // px before activating drag

  constructor(scene: Phaser.Scene) {
    this.dragRect = scene.add.graphics().setDepth(200).setScrollFactor(1);
  }

  addSoldier(soldier: Soldier): void {
    this.soldiers.push(soldier);
  }

  getSoldiers(): Soldier[] {
    return this.soldiers;
  }

  getLiveSoldiers(): Soldier[] {
    return this.soldiers.filter((s) => !s.isDead());
  }

  getSelected(): Soldier[] {
    return [...this.selected].filter((s) => !s.isDead());
  }

  /**
   * Call from GameScene's pointerdown handler (left-click).
   * Returns true if the squad manager consumed the click (soldier selected).
   */
  handlePointerDown(pointer: Phaser.Input.Pointer): boolean {
    // Record drag start
    this.dragStart = new Phaser.Math.Vector2(pointer.worldX, pointer.worldY);
    this.isDragging = false;
    return false; // don't consume yet — wait for up
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragStart || !pointer.leftButtonDown()) return;

    const dist = Phaser.Math.Distance.Between(
      this.dragStart.x, this.dragStart.y,
      pointer.worldX, pointer.worldY
    );

    if (dist > this.DRAG_THRESHOLD) {
      this.isDragging = true;
      this.drawDragRect(pointer.worldX, pointer.worldY);
    }
  }

  /**
   * Returns true if the event was fully handled (no movement should trigger).
   */
  handlePointerUp(
    pointer: Phaser.Input.Pointer,
    isShift: boolean
  ): boolean {
    if (!this.dragStart) return false;

    const endX = pointer.worldX;
    const endY = pointer.worldY;

    if (this.isDragging) {
      // Finalise drag-select
      this.dragRect.clear();
      const rect = new Phaser.Geom.Rectangle(
        Math.min(this.dragStart.x, endX),
        Math.min(this.dragStart.y, endY),
        Math.abs(endX - this.dragStart.x),
        Math.abs(endY - this.dragStart.y)
      );
      this.selectInRect(rect);
      this.dragStart = null;
      this.isDragging = false;
      return true; // consumed
    }

    this.dragStart = null;

    // Click: check if clicked on a soldier
    for (const soldier of this.getLiveSoldiers()) {
      const dist = Phaser.Math.Distance.Between(endX, endY, soldier.x, soldier.y);
      if (dist <= 16) {
        if (isShift) {
          this.toggleSelection(soldier);
        } else {
          this.selectOnly(soldier);
        }
        return true;
      }
    }

    // Clicked on empty ground
    // If units are selected, pass through to movement handler (do NOT deselect)
    if (this.selected.size > 0) {
      return false; // selected units will move
    }

    // Nothing selected — deselect all (clears any stale state) and pass through
    if (!isShift) {
      this.deselectAll();
    }

    return false; // pass to movement handler
  }

  private drawDragRect(worldX: number, worldY: number): void {
    if (!this.dragStart) return;
    this.dragRect.clear();
    this.dragRect.lineStyle(1, 0x00ff88, 0.8);
    this.dragRect.fillStyle(0x00ff88, 0.12);
    const x = Math.min(this.dragStart.x, worldX);
    const y = Math.min(this.dragStart.y, worldY);
    const w = Math.abs(worldX - this.dragStart.x);
    const h = Math.abs(worldY - this.dragStart.y);
    this.dragRect.fillRect(x, y, w, h);
    this.dragRect.strokeRect(x, y, w, h);
  }

  private selectInRect(rect: Phaser.Geom.Rectangle): void {
    this.deselectAll();
    for (const soldier of this.getLiveSoldiers()) {
      if (rect.contains(soldier.x, soldier.y)) {
        this.selected.add(soldier);
        soldier.setSelected(true);
      }
    }
  }

  selectOnly(soldier: Soldier): void {
    this.deselectAll();
    this.selected.add(soldier);
    soldier.setSelected(true);
  }

  toggleSelection(soldier: Soldier): void {
    if (this.selected.has(soldier)) {
      this.selected.delete(soldier);
      soldier.setSelected(false);
    } else {
      this.selected.add(soldier);
      soldier.setSelected(true);
    }
  }

  selectAll(): void {
    this.deselectAll();
    for (const soldier of this.getLiveSoldiers()) {
      this.selected.add(soldier);
      soldier.setSelected(true);
    }
  }

  deselectAll(): void {
    for (const soldier of this.selected) {
      soldier.setSelected(false);
    }
    this.selected.clear();
  }

  removeSoldier(soldier: Soldier): void {
    this.selected.delete(soldier);
    const idx = this.soldiers.indexOf(soldier);
    if (idx !== -1) this.soldiers.splice(idx, 1);
  }

  /**
   * Move all selected soldiers to `target`, spreading them in formation.
   * Returns an array of targets (one per selected soldier) for the caller to use.
   */
  getFormationTargets(targetX: number, targetY: number): { soldier: Soldier; tx: number; ty: number }[] {
    const selected = this.getSelected();
    return selected.map((soldier, idx) => {
      const offset = FORMATION_OFFSETS[idx % FORMATION_OFFSETS.length];
      return {
        soldier,
        tx: targetX + offset.x,
        ty: targetY + offset.y,
      };
    });
  }
}
